/**
 * 選手検索の絞り込みロジック (SPEC §3 F-01「選手名検索または一覧から」)
 *
 * 選手名（漢字）・よみがな（ひらがな）に対する部分一致で選手を絞り込む。
 * ひらがな/カタカナの入力ゆらぎを吸収するため、比較前にカタカナをひらがなへ正規化する
 * （ユーザーがカタカナで入力してもひらがなのよみがなと一致させられるようにするため）。
 *
 * 外部依存・入出力・現在時刻の取得を一切持たない純粋関数（CLAUDE.md 禁止事項6 / SPEC §7.4）。
 */
import type { Player } from './player'

/** 全角カタカナ（ァ-ヶ）を対応するひらがなへ変換する。それ以外の文字はそのまま返す */
function katakanaToHiragana(input: string): string {
  return input.replace(/[ァ-ヶ]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) - 0x60),
  )
}

/**
 * 検索クエリ・比較対象の文字列を正規化する。
 * 空白（前後だけでなく文字間も）をすべて除去し、カタカナをひらがなへ変換し、小文字化する。
 *
 * 文字間の空白も除去するのは、npb.jp の選手名が姓名間に半角スペースを含む
 * （例: "岩崎 優"。`parsePlayerPage.ts` / `parseRosterPage.ts` の正規化仕様）ため、
 * スペース無しで検索しても一致できるようにするため（Issue #9 で実データ統合した際に
 * 発見。モックデータの氏名にはスペースが無かったため見えていなかった）。
 */
function normalize(input: string): string {
  return katakanaToHiragana(input.replace(/\s+/g, '').toLowerCase())
}

/**
 * 選手一覧をクエリで絞り込む。
 *
 * - クエリが空文字列（前後の空白のみを含む）のときは、絞り込まずに全件を返す
 * - `player.name`（漢字）・`player.nameKana`（ひらがな）のいずれかに部分一致すれば採用する
 * - 一致が無ければ空配列を返す
 *
 * @param players 絞り込み対象の選手一覧（変更しない）
 * @param query 検索クエリ（ユーザー入力そのまま）
 */
export function searchPlayers<T extends Player>(
  players: readonly T[],
  query: string,
): T[] {
  const normalizedQuery = normalize(query)
  if (normalizedQuery === '') {
    return players.slice()
  }

  return players.filter((player) => {
    const normalizedName = normalize(player.name)
    // npb.jp のロースター一覧ページにはよみがなが含まれないため null になりうる
    // （選手個別ページ取得後に判明する。Issue #9）。null の場合は氏名のみで判定する。
    const normalizedKana = normalize(player.nameKana ?? '')
    return (
      normalizedName.includes(normalizedQuery) ||
      normalizedKana.includes(normalizedQuery)
    )
  })
}
